import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Row, Col, Alert, Spinner, InputGroup
} from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaPlus, FaMinus, FaSearch } from 'react-icons/fa';
import { calculatorAreaAPI } from '../../api/apiClient';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import { useSearchFilter } from '../useSearchFilter';

function CityPriceTable() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  // const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    group_name: '',
    postal_codes: [''],
  });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const filteredData = useSearchFilter(data, searchTerm, ['group_name', 'postal_codes']);

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await calculatorAreaAPI.index({
        page,
        per_page: pagination.per_page
      });

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        // setFilteredData(fetchedData); // Initialize filtered data
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.length
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_fetch_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_fetch_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openForm = (entry = null) => {
    if (entry) {
      const postalCodesArray = Array.isArray(entry.postal_codes) 
        ? entry.postal_codes 
        : (entry.postal_codes || '').split(',').map(code => code.trim()).filter(code => code !== '');
      setForm({
        group_name: entry.group_name || '',
        postal_codes: postalCodesArray.length > 0 ? postalCodesArray : [''],
      });
      setEditData(entry);
    } else {
      setForm({
        group_name: '',
        postal_codes: [''],
      });
      setEditData(null);
    }
    setShowForm(true);
  };

  const addPostalCode = () => {
    setForm(prev => ({
      ...prev,
      postal_codes: [...prev.postal_codes, '']
    }));
  };

  const removePostalCode = (index) => {
    if (form.postal_codes.length > 1) {
      setForm(prev => ({
        ...prev,
        postal_codes: prev.postal_codes.filter((_, i) => i !== index)
      }));
    }
  };

  const updatePostalCode = (index, value) => {
    setForm(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.map((code, i) => i === index ? value : code)
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Filter empty postal codes
      // const postalCodesArray = form.postal_codes
      //   .map(code => code.trim())
      //   .filter(code => code !== '');

      // if (postalCodesArray.length === 0) {
      //   Swal.fire({
      //     icon: 'warning',
      //     title: t('warning'),
      //     text: 'Please add at least one postal code',
      //     timer: 2000,
      //     showConfirmButton: false
      //   });
      //   return;
      // }

      const formData = {
        ...form,
        postal_codes: form.postal_codes.split(',').map(code => code.trim()).filter(code => code !== '')
      };

      const fd = new FormData();
      fd.append('group_name', form.group_name);
      
      // Append postal codes as array
      // postalCodesArray.forEach((code, index) => {
      //   fd.append(`postal_codes[${index}]`, code);
      // });

      let response;
      if (editData) {
        response = await calculatorAreaAPI.update(editData.id, formData);
      } else {
        response = await calculatorAreaAPI.store(formData);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_saved_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowForm(false);
        fetchData(pagination.current_page);
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_save_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_save_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error saving data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (id) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await calculatorAreaAPI.delete(deleteId);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_deleted_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowDelete(false);
        fetchData(pagination.current_page);
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_delete_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_delete_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error deleting data:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="dashboard-title">{t('calculator_areas')}</h3>
        <Button className='add-btn' onClick={() => openForm()}>
          <FaPlus className="me-1" /> {t('Neu_hinzufügen')}
        </Button>
      </div>

      {/* Search Bar */}
      
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t('search_group_postal')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          {searchTerm && (
            <Button 
              variant="outline-secondary" 
              onClick={clearSearch}
              title={t("reset_search")}
            >
              ✕
            </Button>
          )}
          </InputGroup>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : filteredData.length === 0 ? (
        <Alert variant="info">
          {searchTerm ? 
            `Keine Ergebnisse gefunden für "${searchTerm}"` : 
            t('no_data_available')
          }
        </Alert>
      ) : (
        <div className="table-container shadow-sm rounded">
          <div className='table-responsive'>
            <Table bordered hover className="custom-table mb-0">
              <thead className="table-dark text-center">
                <tr>
                  <th>#</th>
                  <th>{t('groupname')}</th>
                  <th>{t('postal_code')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((entry, idx) => (
                  <tr key={entry.id} className="text-center">
                    <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                    <td>
                      {searchTerm ? (
                        <span 
                          dangerouslySetInnerHTML={{
                            __html: entry.group_name?.replace(
                              new RegExp(searchTerm, 'gi'), 
                              match => `<mark class="bg-warning">${match}</mark>`
                            )
                          }} 
                        />
                      ) : (
                        entry.group_name
                      )}
                    </td>
                    <td>
                      <div style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }}>
                        {Array.isArray(entry.postal_codes) ? 
                          entry.postal_codes.map(code => 
                            searchTerm ? (
                              <span 
                                key={code}
                                dangerouslySetInnerHTML={{
                                  __html: code.toString().replace(
                                    new RegExp(searchTerm, 'gi'), 
                                    match => `<mark class="bg-warning">${match}</mark>`
                                  )
                                }} 
                              />
                            ) : code
                          ).reduce((prev, curr) => [prev, ', ', curr]) 
                          : entry.postal_codes
                        }
                      </div>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        className="me-2 add-btn"
                        onClick={() => openForm(entry)}
                        title="Bearbeiten"
                        disabled={loading}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openDelete(entry.id)}
                        title={t("Delete")}
                        disabled={loading}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      
      {!loading && filteredData.length > 0 && pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchData}
          disabled={loading}
        />
      )}

      {/* باقي الكود بدون تغيير (Modals) */}
      {/* Add/Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editData ? t('Edit') : t('Add')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3 gy-2">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t("group_name")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.group_name}
                    onChange={e => setForm(prev => ({ ...prev, group_name: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              {/* <Col md={12}>
                <Form.Group>
                  <Form.Label>{t("postal_code")}</Form.Label>
                  <div className="postal-codes-container d-flex align-items-center flex-wrap">
                    {form.postal_codes.map((code, index) => (
                      <div key={index} className="d-flex align-items-center col-3 m-2">
                        <Form.Control
                          type="text"
                          value={code}
                          onChange={(e) => updatePostalCode(index, e.target.value)}
                          placeholder={`Postleitzahl ${index + 1}`}
                          className="me-2"
                          style={{ flex: 1 }}
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removePostalCode(index)}
                          disabled={form.postal_codes.length <= 1 || loading}
                        >
                          <FaMinus />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-2"
                    onClick={addPostalCode}
                    disabled={loading}
                  >
                    <FaPlus /> {t("add_another_postal_code")}
                  </Button>
                  <br />
                  <Form.Text className="text-muted">
                    {t("postal_code_note")}
                  </Form.Text>
                </Form.Group>
              </Col> */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t("postal_code")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    type="text"
                    value={form.postal_codes}
                    onChange={e => setForm(prev => ({ ...prev, postal_codes: e.target.value }))}
                    placeholder="50667, 50668, 50670"
                  />
                  <Form.Text className="text-muted">
                    {t("postal_code_note")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={loading}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading || !form.group_name.trim()}>
            {loading ? t('Saving...') : t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_group")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('Deleting...') : t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .postal-codes-container {
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 1rem;
          background-color: #f8f9fa;
        }
        
        mark {
          background-color: #ffc107;
          padding: 0.1rem 0.2rem;
          border-radius: 0.2rem;
        }
      `}</style>
    </div>
  );
}

export default CityPriceTable;